import type { StepperProps as MuiStepperProps, StepIconProps, SxProps, Theme } from '@mui/material';
import {
  StepButton,
  StepConnector,
  stepLabelClasses,
  stepConnectorClasses,
  Stepper as MuiStepper,
  styled,
  StepLabel,
} from '@mui/material';

import Step from '@mui/material/Step';
import type { FC, MouseEventHandler } from 'react';
import { useDeviceType } from '../../hooks';

export type StepperProps = MuiStepperProps & {
  steps: string[];
  activeStep: number;
  icons: { [index: string]: React.ReactElement };
  handleOnClick?: (index: number) => MouseEventHandler | undefined;
};

const SxPropsStepper: SxProps<Theme> = (theme) => ({
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: theme.palette.primary.main,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: theme.palette.primary.main,
    },
  },
  [`&.${stepConnectorClasses.root}`]: {
    top: { xs: '0', md: '-0.5rem' },
    flex: '1 1 auto',
    position: 'relative',
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: { xs: '2px', md: '3px' },
    border: 0,
    backgroundColor: theme.palette.grey.main,
    borderRadius: 1,
  },
});

const StepIconRoot = styled('div')<{
  ownerState: { completed?: boolean; active?: boolean };
}>(({ theme, ownerState }) => ({
  zIndex: 1,
  color: theme.palette.grey.main,
  width: 50,
  height: 50,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  ...(ownerState.active && {
    color: theme.palette.text.primary,
  }),
  ...(ownerState.completed && {
    color: theme.palette.text.dark,
  }),
}));

const StepMobileRoot = styled('div')<{
  ownerState: { completed?: boolean; active?: boolean };
}>(({ theme, ownerState }) => ({
  zIndex: 1,
  backgroundColor: theme.palette.grey.main,
  width: 16,
  height: 16,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  ...(ownerState.active && {
    backgroundColor: theme.palette.primary.main,
  }),
  ...(ownerState.completed && {
    backgroundColor: theme.palette.primary.main,
  }),
}));

const SxPropsStepperLabel: SxProps = () => ({
  [`.${stepLabelClasses.iconContainer}`]: { paddingRight: '0' },
  flexDirection: 'column',
  flex: 0,
});

const StepIcons = (props: StepIconProps) => {
  const { active, completed, className, icon } = props;
  const { isMobile } = useDeviceType();

  return isMobile ? (
    <StepMobileRoot ownerState={{ completed, active }} className={className} />
  ) : (
    <StepIconRoot ownerState={{ completed, active }} className={className}>
      {icon}
    </StepIconRoot>
  );
};

export const Stepper: FC<StepperProps> = ({ steps, activeStep, icons, handleOnClick }) => {
  const { isMobile } = useDeviceType();

  return (
    <MuiStepper
      activeStep={activeStep}
      nonLinear
      connector={<StepConnector sx={SxPropsStepper} />}
      sx={{ justifyContent: 'space-between', gap: '0.5rem' }}
    >
      {steps.map((label, index) => (
        <Step key={label} completed={index <= activeStep} component="div" sx={{ padding: '0' }}>
          {handleOnClick ? (
            <StepButton
              onClick={() => handleOnClick(index)}
              sx={{ display: 'block', padding: '0', margin: '0' }}
            >
              <StepLabel
                sx={SxPropsStepperLabel}
                StepIconComponent={() => (
                  <StepIcons icon={icons[index + 1]} active={index <= activeStep} />
                )}
              >
                {isMobile ? undefined : label}
              </StepLabel>
            </StepButton>
          ) : (
            <StepLabel
              sx={SxPropsStepperLabel}
              StepIconComponent={() => (
                <StepIcons icon={icons[index + 1]} active={index <= activeStep} />
              )}
            >
              {isMobile ? undefined : label}
            </StepLabel>
          )}
        </Step>
      ))}
    </MuiStepper>
  );
};
